import { Text } from "@gravity-ui/uikit";
import { forwardRef, useCallback, useMemo, useRef } from "react";

import { HighlightSegment } from "./HighlightSegment";
import css from "./TemplateChipEditor.module.scss";
import {
  buildSegments,
  collectPlaceholders,
  type InvalidRange,
} from "./TemplateChipEditor.utils";
import { useScrollSync } from "./useScrollSync";
import {
  type TextareaInsertHandle,
  useTextareaInsertHandle,
} from "./useTextareaInsertHandle";

export type TemplateChipEditorHandle = TextareaInsertHandle;
export type { InvalidRange } from "./TemplateChipEditor.utils";
export { collectPlaceholders } from "./TemplateChipEditor.utils";

interface TemplateChipEditorProps {
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  isValid: (path: string) => boolean;
  invalidRanges?: InvalidRange[];
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  maxLength?: number;
  rows?: number;
  id?: string;
  disabled?: boolean;
}

// Chip-based редактор: текст редактируется в обычном textarea, поверх абсолютно
// позиционирован «зеркальный» слой, оборачивающий {{name}} в визуальные чипы
// (зелёные — валидные, красные — неизвестные/syntax error). Перекрытие точно
// синхронизировано через одинаковые шрифт/padding/letter-spacing и общий scroll.
export const TemplateChipEditor = forwardRef<
  TemplateChipEditorHandle,
  TemplateChipEditorProps
>(function TemplateChipEditor(
  {
    value,
    onChange,
    onBlur,
    placeholder,
    isValid,
    invalidRanges,
    ariaInvalid,
    ariaDescribedBy,
    maxLength,
    rows = 3,
    id,
    disabled,
  },
  ref,
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const segments = useMemo(
    () =>
      buildSegments(value, collectPlaceholders(value), invalidRanges, isValid),
    [value, invalidRanges, isValid],
  );

  const syncScroll = useScrollSync(textareaRef, highlightRef, value);
  useTextareaInsertHandle(ref, { textareaRef, value, onChange });

  const handleChipClick = useCallback((end: number) => {
    const el = textareaRef.current;
    if (!el) return;
    // Клик на чип отрабатывается у overlay'a, не у textarea. Возвращаем фокус
    // и ставим каретку после плейсхолдера, иначе юзер останется без курсора.
    el.focus();
    el.setSelectionRange(end, end);
  }, []);

  return (
    <div className={css.wrapper} data-disabled={disabled || undefined}>
      <div className={css.editor}>
        <div className={css.highlight} ref={highlightRef} aria-hidden="true">
          {segments.map((seg, i) => (
            <HighlightSegment
              key={i}
              segment={seg}
              onInvalidChipClick={handleChipClick}
            />
          ))}
          {/* zero-width замыкатель: даёт подсветке расти вместе с textarea
              после последней пустой строки. */}
          {"​"}
        </div>
        <textarea
          id={id}
          ref={textareaRef}
          className={css.textarea}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={syncScroll}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          spellCheck={false}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
        />
      </div>
      {maxLength !== undefined && (
        <div className={css.counter}>
          <Text variant="caption-2" color="hint">
            {value.length} / {maxLength}
          </Text>
        </div>
      )}
    </div>
  );
});
